import type { PixelPoint } from "./types";

function round(n: number): number {
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
}

/**
 * Build an SVG path `d` that draws a smooth curve through `points` using a
 * Catmull-Rom → cubic-Bézier conversion. Returns "" for fewer than 2 points
 * (nothing to draw) and a straight segment for exactly 2.
 *
 * The curve is solid — apply dashing via the `<path>`'s `stroke-dasharray`.
 */
export function buildSmoothPath(points: PixelPoint[], tension = 1): string {
  if (points.length < 2) return "";
  const [first] = points;
  if (points.length === 2) {
    const [, second] = points;
    return `M ${round(first.x)} ${round(first.y)} L ${round(second.x)} ${round(second.y)}`;
  }

  let d = `M ${round(first.x)} ${round(first.y)}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] ?? points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] ?? p2;
    const c1x = p1.x + ((p2.x - p0.x) / 6) * tension;
    const c1y = p1.y + ((p2.y - p0.y) / 6) * tension;
    const c2x = p2.x - ((p3.x - p1.x) / 6) * tension;
    const c2y = p2.y - ((p3.y - p1.y) / 6) * tension;
    d += ` C ${round(c1x)} ${round(c1y)} ${round(c2x)} ${round(c2y)} ${round(p2.x)} ${round(p2.y)}`;
  }
  return d;
}
